import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch, api } from '../lib/api';
import { WeatherIcon } from '../components/ui';
import { usePeople } from '../context/PeopleContext';
import { getEventHero } from '../lib/eventHero';

function fmt12local(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2,'0')} ${ampm}`;
}

function getDuration(start, end) {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const dur = (eh * 60 + em) - (sh * 60 + sm);
  if (dur <= 0) return null;
  const hrs = Math.floor(dur / 60);
  const mins = dur % 60;
  return hrs > 0 ? `${hrs}h${mins > 0 ? ` ${mins}m` : ''}` : `${mins}m`;
}

function getFreeTime(endTime, nextStartTime) {
  if (!endTime || !nextStartTime) return null;
  const [eh, em] = endTime.split(':').map(Number);
  const [nh, nm] = nextStartTime.split(':').map(Number);
  const diff = (nh * 60 + nm) - (eh * 60 + em);
  if (diff <= 0) return null;
  const hrs = Math.floor(diff / 60);
  const mins = diff % 60;
  return hrs > 0 ? `${hrs}h ${mins > 0 ? mins + 'm ' : ''}free` : `${mins}m free`;
}

export default function EventDetail({ isAdmin }) {
  const params = useParams();
  const sheetId = params.sheetId || params.id;
  const eventId = params.eventId;
  const navigate = useNavigate();
  const [sheet, setSheet] = useState(null);
  const [locWeather, setLocWeather] = useState(null);
  const { colorMap } = usePeople();
  const [event, setEvent] = useState(null);
  const [prevEvent, setPrevEvent] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    if (!sheetId || !eventId) return;
    apiFetch(`/sheets/${sheetId}`).then(data => {
      setSheet(data);
      const events = (data.events || []).sort((a, b) => {
        if (!a.timeStart && !b.timeStart) return 0;
        if (!a.timeStart) return 1;
        if (!b.timeStart) return -1;
        return a.timeStart.localeCompare(b.timeStart);
      });
      const idx = events.findIndex(e => e.id === eventId);
      if (idx !== -1) {
        setEvent(events[idx]);
        setPrevEvent(idx > 0 ? events[idx - 1] : null);
        setNextEvent(idx < events.length - 1 ? events[idx + 1] : null);
      }
    }).catch(err => { console.error("EventDetail fetch error:", err); setError(err.message); }).finally(() => setLoading(false));
  }, [sheetId, eventId]);

  useEffect(() => {
    if (!event || !event.location || !sheet || !sheet.date) return;
    apiFetch('/sheets/weather?location=' + encodeURIComponent(event.location) + '&date=' + sheet.date)
      .then(w => { if (w && w.tempHigh) setLocWeather(w); })
      .catch(() => {});
  }, [event?.location, sheet?.date]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', background:'var(--bg)' }}>
      <div style={{ width:32, height:32, border:'3px solid var(--border)', borderTop:'3px solid var(--blue)', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
    </div>
  );

  if (error) return <div style={{ padding:40, color:'var(--red)' }}>Error: {error}</div>;
  if (!event) return <div style={{ padding:40, color:'var(--text2)' }}>Event not found.</div>;

  const isZoom  = event.url?.includes('zoom.us');
  const isMeet  = event.url?.includes('meet.google.com');
  const isTeams = event.url?.includes('teams.microsoft.com') || event.url?.includes('teams.live.com');
  const linkLabel = isZoom ? 'Join Zoom' : isMeet ? 'Join Meet' : isTeams ? 'Join Teams' : 'Open Link';
  const linkColor = isZoom ? '#2D8CFF' : isMeet ? '#34A853' : isTeams ? '#6264A7' : 'var(--blue)';
  const linkBg    = isZoom ? 'rgba(45,140,255,0.15)' : isMeet ? 'rgba(52,168,83,0.15)' : isTeams ? 'rgba(98,100,167,0.15)' : 'rgba(79,156,249,0.15)';
  const linkBdr   = isZoom ? '1px solid rgba(45,140,255,0.4)' : isMeet ? '1px solid rgba(52,168,83,0.4)' : isTeams ? '1px solid rgba(98,100,167,0.4)' : '1px solid rgba(79,156,249,0.4)';

  const duration = getDuration(event.timeStart, event.timeEnd);
  const freeTime = getFreeTime(event.timeEnd, nextEvent?.timeStart);
  const dateStr = sheet?.date ? new Date(sheet.date + 'T12:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' }) : '';

  // Get first person color
  const persons = (event.person||'').split(',').map(p=>p.trim()).filter(Boolean);
  const firstPersonKey = persons.length ? Object.keys(colorMap).find(k => k.toLowerCase() === persons[0].toLowerCase()) : null;
  const accentColor = firstPersonKey ? colorMap[firstPersonKey] : 'var(--blue)';

  // Parse driver/greeter from notes
  const noteLines = (event.notes||'').split('\n').map(l => l.trim()).filter(Boolean);
  const contactLines = noteLines.filter(l => /^(Driver|Greeter):/i.test(l));
  const otherNotes = noteLines.filter(l => !/^(Driver|Greeter):/i.test(l)).join('\n');

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', maxWidth:560, margin:'0 auto', paddingBottom:40 }}>

      {/* Smart Hero */}
      {(() => {
        const hero = getEventHero(event.title, event.location, event.url);
        const hasMap = event.location && hero.type === 'map';
        const hasBrand = hero.type === 'brand';
        const hasKeyword = hero.type === 'keyword';
        const hasHero = hasMap || hasBrand || hasKeyword;

        return (
          <div style={{ height: hasHero ? 200 : 0, position:'relative', overflow:'hidden' }}>
            {/* Background layer */}
            {hasMap ? (
              <iframe title="Map" src={`https://www.google.com/maps?q=${encodeURIComponent(event.location)}&output=embed&z=14`}
                style={{ width:'100%', height:'100%', border:'none', filter:'brightness(0.8) contrast(1.1) saturate(0.7)', pointerEvents:'none' }} loading="lazy" />
            ) : (hasBrand || hasKeyword) ? (
              <div style={{ width:'100%', height:'100%', background:hero.gradient, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <i className={`fa-solid ${hero.icon}`} style={{ fontSize:56, color:'rgba(255,255,255,0.15)' }}></i>
              </div>
            ) : null}

            {/* Gradient overlay */}
            {hasHero && (
              <div style={{ position:'absolute', bottom:0, left:0, right:0, height:80, background:'linear-gradient(transparent, var(--bg))' }} />
            )}

            {/* Back button */}
            {hasHero && (
              <button onClick={() => navigate(-1)}
                style={{ position:'absolute', top:14, left:14, width:36, height:36, borderRadius:12, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(10px)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontSize:14 }}>
                <i className="fa-solid fa-chevron-left"></i>
              </button>
            )}

            {/* Brand label */}
            {hasBrand && (
              <div style={{ position:'absolute', bottom:16, left:20, display:'flex', alignItems:'center', gap:8 }}>
                <i className={`fa-solid ${hero.icon}`} style={{ fontSize:18, color:'white' }}></i>
                <span style={{ fontSize:16, fontWeight:800, color:'white', letterSpacing:-0.3 }}>{hero.label}</span>
              </div>
            )}

            {/* Directions badge */}
            {hasMap && (
              <a href={`https://maps.apple.com/?q=${encodeURIComponent(event.location)}`} target="_blank" rel="noreferrer"
                style={{ position:'absolute', top:14, right:14, background:'rgba(0,0,0,0.5)', backdropFilter:'blur(10px)', borderRadius:8, padding:'6px 10px', display:'flex', alignItems:'center', gap:4, textDecoration:'none' }}>
                <i className="fa-solid fa-diamond-turn-right" style={{ fontSize:10, color:'white' }}></i>
                <span style={{ fontSize:11, fontWeight:700, color:'white' }}>Directions</span>
              </a>
            )}
          </div>
        );
      })()}

      {/* Plain back bar if no hero */}
      {!event.location && getEventHero(event.title, event.location, event.url).type !== 'brand' && getEventHero(event.title, event.location, event.url).type !== 'keyword' && (
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)' }}>
          <button onClick={() => navigate(-1)}
            style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', cursor:'pointer', color:'var(--blue)', fontSize:14, fontWeight:600, fontFamily:'inherit', padding:0 }}>
            <i className="fa-solid fa-chevron-left" style={{ fontSize:11 }}></i> Back
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ padding:'20px 20px 16px', borderBottom:'1px solid var(--border)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
          <span style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', textTransform:'uppercase' }}>
            {sheet?.artist?.name} · {sheet?.date ? new Date(sheet.date+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'}) : ''}
          </span>
        </div>
        <h1 style={{ fontSize:24, fontWeight:900, color:'var(--text)', margin:'0 0 8px', lineHeight:1.2, letterSpacing:-0.5 }}>{event.title}</h1>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <span style={{ fontSize:15, color:'var(--text2)', fontWeight:600 }}>
            {event.timeStart ? fmt12local(event.timeStart) : 'All day'}
            {event.timeEnd ? ` – ${fmt12local(event.timeEnd)}` : ''}
          </span>
          {duration && <span style={{ fontSize:12, color:'var(--text3)', background:'rgba(255,255,255,0.04)', borderRadius:8, padding:'2px 10px', border:'1px solid var(--border)' }}>{duration}</span>}
        </div>
        <p style={{ fontSize:13, color:'var(--text3)', margin:'6px 0 0' }}>{dateStr}</p>

        {/* Person badges */}
        {persons.length > 0 && (
          <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
            {persons.map((p,i) => {
              const key = Object.keys(colorMap).find(k => k.toLowerCase() === p.toLowerCase());
              const c = key ? colorMap[key] : '#94a3b8';
              return <span key={i} style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:16, background:c+'14', border:`1px solid ${c}30`, color:c, fontSize:12, fontWeight:700 }}><span style={{ width:6, height:6, borderRadius:'50%', background:c }} />{p}</span>;
            })}
          </div>
        )}

        {/* Weather */}
        {(locWeather || sheet?.weather) && (() => {
          const w = locWeather || sheet.weather;
          const loc = locWeather ? (event.location||'').split(',')[0] : (sheet.city||'').replace(', USA','');
          return (
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, marginTop:10, background:'rgba(255,255,255,0.03)', border:'1px solid var(--border)', borderRadius:10, padding:'6px 12px' }}>
              <WeatherIcon condition={w.condition} size={14} />
              <span style={{ fontSize:12, fontWeight:600, color:'var(--text)' }}>{w.tempHigh}°/{w.tempLow}°</span>
              <span style={{ fontSize:11, color:'var(--text3)' }}>{w.condition}{loc?' · '+loc:''}</span>
            </div>
          );
        })()}
      </div>

      {/* Action buttons */}
      <div style={{ display:'flex', gap:8, padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
        {event.location && (
          <a href={`https://maps.apple.com/?q=${encodeURIComponent(event.location)}`} target="_blank" rel="noreferrer"
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', borderRadius:12, background:'var(--blue)', color:'white', fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:'inherit' }}>
            <i className="fa-solid fa-diamond-turn-right" style={{ fontSize:12 }}></i> Directions
          </a>
        )}
        {event.url && (
          <a href={event.url} target="_blank" rel="noreferrer"
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', borderRadius:12,
              background:linkBg, border:linkBdr, color:linkColor, fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:'inherit' }}>
            <i className={`fa-solid ${isZoom?'fa-video':isMeet?'fa-video':isTeams?'fa-headset':'fa-link'}`} style={{ fontSize:12 }}></i> {linkLabel}
          </a>
        )}
        {isAdmin && !event.gcalEventId && (
          <button onClick={async () => {
            setPushing(true);
            try { await api.pushToGCal(event.id); setEvent(prev => ({...prev, gcalEventId:'synced'})); }
            catch(e) { alert('GCal sync failed: '+e.message); }
            finally { setPushing(false); }
          }} disabled={pushing}
            style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'12px', borderRadius:12,
              background:'rgba(30,64,175,0.08)', border:'1px solid rgba(30,64,175,0.2)', color:'var(--blue)', fontWeight:700, fontSize:14, cursor:'pointer', fontFamily:'inherit', opacity:pushing?0.5:1 }}>
            <i className="fa-brands fa-google" style={{ fontSize:12 }}></i> {pushing?'...':'GCal'}
          </button>
        )}
      </div>

      {/* Info rows */}
      <div>
        {event.location && (
          <a href={`https://maps.apple.com/?q=${encodeURIComponent(event.location)}`} target="_blank" rel="noreferrer"
            style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 20px', borderBottom:'1px solid var(--border)', textDecoration:'none' }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'rgba(30,64,175,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <i className="fa-solid fa-location-dot" style={{ fontSize:14, color:'var(--blue)' }}></i>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ fontSize:11, fontWeight:600, color:'var(--text3)', letterSpacing:1, margin:0 }}>LOCATION</p>
              <p style={{ fontSize:15, fontWeight:600, color:'var(--blue)', margin:'2px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{event.location}</p>
            </div>
            <i className="fa-solid fa-chevron-right" style={{ fontSize:11, color:'var(--text3)' }}></i>
          </a>
        )}

        {otherNotes && (
          <div style={{ display:'flex', alignItems:'flex-start', gap:14, padding:'14px 20px', borderBottom:'1px solid var(--border)' }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'rgba(255,255,255,0.04)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <i className="fa-regular fa-note-sticky" style={{ fontSize:14, color:'var(--text3)' }}></i>
            </div>
            <div style={{ flex:1 }}>
              <p style={{ fontSize:11, fontWeight:600, color:'var(--text3)', letterSpacing:1, margin:0 }}>NOTES</p>
              <p style={{ fontSize:14, color:'var(--text2)', lineHeight:1.7, whiteSpace:'pre-line', margin:'4px 0 0' }}>{otherNotes}</p>
            </div>
          </div>
        )}

        {contactLines.map((line, i) => {
          const isDriver = /^Driver:/i.test(line);
          const val = line.replace(/^(Driver|Greeter):\s*/i, '');
          const phoneMatch = val.match(/(\+?1?[\s\-\.]?\(?[0-9]{3}\)?[\s\-\.][0-9]{3}[\s\-\.][0-9]{4}|[0-9]{10,11})/);
          const phone = phoneMatch ? phoneMatch[0].trim() : null;
          const name = phone ? val.replace(phone, '').replace(/[\s\-·,]+$/, '').trim() : val;
          return (
            <a key={i} href={phone?`tel:${phone.replace(/\D/g,'')}`:'#'}
              style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 20px', borderBottom:'1px solid var(--border)', textDecoration:'none' }}>
              <div style={{ width:36, height:36, borderRadius:10, background: isDriver?'rgba(30,64,175,0.1)':'rgba(251,191,36,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <i className={`fa-solid ${isDriver?'fa-car':'fa-hand-wave'}`} style={{ fontSize:14, color: isDriver?'var(--blue)':'#fbbf24' }}></i>
              </div>
              <div style={{ flex:1 }}>
                <p style={{ fontSize:11, fontWeight:600, color:'var(--text3)', letterSpacing:1, margin:0 }}>{isDriver?'DRIVER':'GREETER'}</p>
                <p style={{ fontSize:15, fontWeight:600, color: phone?'#22c55e':'var(--text)', margin:'2px 0 0' }}>{name||val}{phone?' · '+phone:''}</p>
              </div>
              {phone && <i className="fa-solid fa-phone" style={{ fontSize:11, color:'#22c55e' }}></i>}
            </a>
          );
        })}

        {event.gcalEventId && (
          <div style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 20px', borderBottom:'1px solid var(--border)' }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'rgba(34,197,94,0.1)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <i className="fa-solid fa-circle-check" style={{ fontSize:14, color:'#22c55e' }}></i>
            </div>
            <div>
              <p style={{ fontSize:11, fontWeight:600, color:'var(--text3)', letterSpacing:1, margin:0 }}>GOOGLE CALENDAR</p>
              <p style={{ fontSize:14, fontWeight:600, color:'#22c55e', margin:'2px 0 0' }}>Synced</p>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      {(prevEvent || nextEvent) && (
        <div style={{ margin:'12px 0', borderTop:'1px solid var(--border)' }}>
          <div style={{ padding:'14px 20px 8px' }}>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', margin:0 }}>
              <i className="fa-regular fa-clock" style={{ marginRight:5, fontSize:9 }}></i>UP NEXT
            </p>
          </div>
          {prevEvent && (
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'6px 20px', opacity:0.4 }}>
              <span style={{ fontSize:11, color:'var(--text3)', minWidth:56, fontWeight:600, textAlign:'right' }}>{prevEvent.timeStart?fmt12local(prevEvent.timeStart):''}</span>
              <div style={{ width:7, height:7, borderRadius:'50%', background:'var(--border)', flexShrink:0 }} />
              <span style={{ fontSize:12, color:'var(--text2)' }}>{prevEvent.title}</span>
            </div>
          )}
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 20px', background:'rgba(30,64,175,0.04)', borderLeft:`3px solid ${accentColor}` }}>
            <span style={{ fontSize:11, color:accentColor, minWidth:53, fontWeight:700, textAlign:'right' }}>{event.timeStart?fmt12local(event.timeStart):''}</span>
            <div style={{ width:7, height:7, borderRadius:'50%', background:accentColor, flexShrink:0 }} />
            <span style={{ fontSize:13, color:'var(--text)', fontWeight:700 }}>{event.title}</span>
          </div>
          {freeTime && (
            <div style={{ padding:'3px 20px 3px 76px' }}>
              <span style={{ fontSize:11, color:'#22c55e', fontWeight:600 }}><i className="fa-solid fa-arrow-down" style={{ fontSize:8, marginRight:4 }}></i>{freeTime}</span>
            </div>
          )}
          {nextEvent && (
            <div style={{ display:'flex', alignItems:'center', gap:12, padding:'6px 20px 10px', opacity:0.4 }}>
              <span style={{ fontSize:11, color:'var(--text3)', minWidth:56, fontWeight:600, textAlign:'right' }}>{nextEvent.timeStart?fmt12local(nextEvent.timeStart):''}</span>
              <div style={{ width:7, height:7, borderRadius:'50%', background:'var(--border)', flexShrink:0 }} />
              <span style={{ fontSize:12, color:'var(--text2)' }}>{nextEvent.title}</span>
            </div>
          )}
        </div>
      )}

      {/* Contacts */}
      {event.contacts?.filter(c => c.name || c.phone).length > 0 && (
        <div style={{ borderTop:'1px solid var(--border)' }}>
          <div style={{ padding:'14px 20px 6px' }}>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', margin:0 }}>
              <i className="fa-regular fa-address-book" style={{ marginRight:5, fontSize:9 }}></i>CONTACTS
            </p>
          </div>
          {event.contacts.filter(c => c.name || c.phone).map((c, i) => (
            <a key={i} href={c.phone?`tel:${c.phone.replace(/\D/g,'')}`:'#'}
              style={{ display:'flex', alignItems:'center', gap:14, padding:'10px 20px', textDecoration:'none' }}>
              <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(30,64,175,0.08)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <i className="fa-solid fa-user" style={{ fontSize:12, color:'var(--blue)' }}></i>
              </div>
              <div style={{ flex:1 }}>
                <p style={{ fontSize:14, fontWeight:600, color:'var(--text)', margin:0 }}>{c.name}</p>
              </div>
              {c.phone && (
                <span style={{ fontSize:12, fontWeight:700, color:'#22c55e' }}>{c.phone}</span>
              )}
            </a>
          ))}
        </div>
      )}

      {/* Files */}
      {event.files?.length > 0 && (
        <div style={{ borderTop:'1px solid var(--border)', padding:'14px 20px' }}>
          <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:10 }}>
            <i className="fa-solid fa-paperclip" style={{ marginRight:5, fontSize:9 }}></i>ATTACHMENTS
          </p>
          <div style={{ display:'flex', gap:8, overflowX:'auto' }}>
            {event.files.map((f, i) => {
              const isPdf = f.type?.includes('pdf');
              const isImage = f.type?.startsWith('image/');
              const openFile = () => {
                if (f.url) { window.open((process.env.REACT_APP_API_URL||'http://localhost:3001')+f.url,'_blank'); return; }
                if (f.data) { const b=atob(f.data.split(',')[1]); const u=new Uint8Array(b.length); for(let j=0;j<b.length;j++) u[j]=b.charCodeAt(j); window.open(URL.createObjectURL(new Blob([u],{type:f.type})),'_blank'); }
              };
              return (
                <button key={i} onClick={openFile}
                  style={{ flexShrink:0, display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'var(--bg)', borderRadius:10, border:'1px solid var(--border)', cursor:'pointer', fontFamily:'inherit' }}>
                  <i className={`fa-solid ${isImage?'fa-image':isPdf?'fa-file-pdf':'fa-file'}`} style={{ fontSize:16, color:accentColor }}></i>
                  <span style={{ fontSize:12, fontWeight:600, color:'var(--text)' }}>{f.name?.replace(/\.[^.]+$/,'')||'File'}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Back button */}
      <div style={{ padding:'16px 20px' }}>
        <button onClick={() => navigate(-1)}
          style={{ width:'100%', padding:'14px', borderRadius:14, background:'var(--card)', border:'1px solid var(--border)', color:'var(--text2)', fontFamily:'inherit', fontSize:15, fontWeight:700, cursor:'pointer' }}>
          Back
        </button>
      </div>
    </div>
  );
}
